import { Component, OnInit, OnDestroy, inject, computed, signal } from '@angular/core';
import { Router } from '@angular/router';
import { IonApp, IonRouterOutlet, ToastController } from '@ionic/angular/standalone';
import { NgxParticlesModule } from '@tsparticles/angular';
import { loadFull } from 'tsparticles';
import { MoveDirection, OutMode, type Engine } from '@tsparticles/engine';
import { SplashScreen } from '@capacitor/splash-screen';
import { AuthService } from './core/services/auth.service';
import { FavoritesService } from './core/services/favorites.service';
import { HistoryService } from './core/services/history.service';
import { RealtimeService } from './core/services/realtime.service';
import { DrawerService } from './core/services/drawer.service';
import { PermissionService } from './core/services/permission.service';
import { BoardingService, ActiveBoarding } from './core/services/boarding.service';
import { PaymentIntentService } from './core/services/payment-intent.service';
import { CityService } from './core/services/city.service';
import { Geolocation } from '@capacitor/geolocation';
import { Html5Qrcode } from 'html5-qrcode';
import QRCode from 'qrcode';
import { SaleSplashComponent } from './shared/sale-splash/sale-splash.component';

interface MenuItem {
  label: string;
  route: string;
  svgPath: string;
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [IonApp, IonRouterOutlet, NgxParticlesModule, SaleSplashComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  readonly showSplash = signal(true);
  readonly appReady   = signal(false);
  private readonly authService       = inject(AuthService);
  private readonly favoritesService  = inject(FavoritesService);
  private readonly historyService    = inject(HistoryService);
  private readonly realtimeService   = inject(RealtimeService);
  private readonly boardingService   = inject(BoardingService);
  readonly drawerService             = inject(DrawerService);
  readonly cityService               = inject(CityService);
  readonly paymentIntent             = inject(PaymentIntentService);
  readonly permissionService         = inject(PermissionService);
  private readonly router            = inject(Router);
  private readonly toastCtrl         = inject(ToastController);

  /** Keep in step with MARKETING_VERSION in ios/App/App.xcodeproj and versionName in android/app/build.gradle. */
  readonly appVersion = '1.0.0';

  readonly currentUser   = this.authService.currentUser;
  readonly isLoggedIn    = computed(() => !!this.currentUser());
  readonly favoriteCount = computed(
    () => this.favoritesService.favorites().length + this.favoritesService.favoriteLines().length
  );
  readonly drawerOpen   = this.drawerService.isOpen;
  readonly overlayOpen  = this.drawerService.overlayOpen;

  // Balance helpers — Number() guards against Prisma Decimal being serialised as a string
  readonly userBalance = computed(() => Number(this.currentUser()?.balance ?? 0));
  readonly payAmountExceedsBalance = computed(() =>
    this.paymentIntent.enabled() &&
    this.paymentIntent.amount() > 0 &&
    this.paymentIntent.amount() > this.userBalance()
  );

  // ── Active boarding (persists across restarts) ──────────────────
  readonly activeBoarding  = signal<ActiveBoarding | null>(null);
  readonly checkoutLoading = signal(false);

  // Ticks every 30 s so the elapsed-time label stays fresh
  private readonly _now        = signal(Date.now());
  private _clockInterval?: ReturnType<typeof setInterval>;

  readonly boardingElapsed = computed(() => {
    const b = this.activeBoarding();
    if (!b) return '';
    const diffMs  = this._now() - new Date(b.scannedAt).getTime();
    const mins    = Math.floor(diffMs / 60_000);
    if (mins < 1)   return 'hace un momento';
    if (mins < 60)  return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h} h ${m} min` : `${h} h`;
  });

  // ── QR Modal state ──────────────────────────────────────────────
  readonly qrModalOpen = signal(false);
  readonly qrTab       = signal<'scan' | 'show'>('scan');

  // Scanner sub-state
  readonly scanState = signal<'scanning' | 'success' | 'error' | 'duplicate'>('scanning');
  readonly scanMsg   = signal('');
  private qrScanner?: Html5Qrcode;
  private scanHandled = false;

  // My QR sub-state
  readonly qrLoading = signal(false);
  readonly qrDataUrl = signal('');
  readonly qrError   = signal('');
  private qrToken    = '';

  readonly menuItems: MenuItem[] = [
    {
      label: 'Mapa',
      route: '/home',
      svgPath: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
    },
    {
      label: 'Líneas',
      route: '/lines',
      svgPath: 'M8 7h8m-8 4h8m-8 4h8M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z',
    },
    {
      label: 'Favoritos',
      route: '/favorites',
      svgPath: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
    },
  ];

  async particlesInit(engine: Engine): Promise<void> { await loadFull(engine); }
  readonly particlesOptions = {
    fullScreen: { enable: false },
    background: { color: { value: 'transparent' } },
    fpsLimit: 60,
    particles: {
      color: { value: '#007580' },
      links: { color: '#007580', distance: 150, enable: true, opacity: 0.4, width: 1 },
      move: { direction: MoveDirection.none, enable: true, outModes: { default: OutMode.bounce }, speed: 1.2 },
      number: { density: { enable: true, area: 800 }, value: 80 },
      opacity: { value: 0.3 },
      shape: { type: 'circle' },
      size: { value: { min: 1, max: 2 } },
    },
    detectRetina: true,
  };

  async ngOnInit() {
    // The web splash (<sale-splash>) is on screen now: hide the native one
    // underneath it so the hand-off is invisible. No-op on the web platform.
    SplashScreen.hide({ fadeOutDuration: 0 }).catch(() => {});

    // Everything here is best-effort. The splash is held up by appReady, and
    // nothing below is needed to show the map and search — so a rejected
    // promise (storage unavailable, no network on a cold start) must not be
    // able to strand the rider on the splash forever.
    try {
      // Must initialize TokenService first so the stored token is loaded from
      // Preferences into memory before loadUserFromToken() checks isLoggedIn().
      await this.authService.init();

      await Promise.allSettled([
        this.authService.loadUserFromToken(),
        this.favoritesService.load(),
        this.historyService.load(),
      ]);
      this.realtimeService.connect();

      // Restore any in-progress boarding from previous session
      const saved = await this.boardingService.loadActiveBoarding();
      if (saved) this.activeBoarding.set(saved);
    } catch (err) {
      console.error('[AppComponent] startup failed, continuing anyway', err);
    }

    // Keep elapsed-time label live
    this._clockInterval = setInterval(() => this._now.set(Date.now()), 30_000);

    this.appReady.set(true);
  }

  /**
   * Called once the splash has fully faded out. Permission dialogs are
   * deferred until now: requesting them earlier (while the splash is still
   * on screen) backgrounds the webview for the native alert, and iOS/WebKit
   * restarts the splash's CSS entrance animations on refocus — making it
   * look like the splash played twice.
   */
  onSplashFinished() {
    this.showSplash.set(false);
    // ensurePermissions() checks the actual device state first and only
    // shows dialogs if location or notifications are not yet granted.
    void this.permissionService.ensurePermissions();
  }

  ngOnDestroy() {
    if (this._clockInterval) clearInterval(this._clockInterval);
  }

  navigate(route: string, queryParams?: Record<string, string>) {
    this.drawerService.close();
    this.router.navigate([route], queryParams ? { queryParams } : undefined);
  }

  isActive(route: string): boolean {
    return this.router.url === route || this.router.url.startsWith(route + '/');
  }

  async checkPermissions() {
    this.drawerService.close();
    // Wait for the drawer slide-out animation (220ms) to finish before
    // presenting any native dialog or Ionic overlay on top of it.
    await new Promise(r => setTimeout(r, 320));
    try {
      const allGranted = await this.permissionService.repairPermissions(true);
      if (allGranted) {
        const toast = await this.toastCtrl.create({
          message: '✓ Permisos concedidos',
          duration: 2500,
          position: 'bottom',
          color: 'success',
        });
        await toast.present();
      }
    } catch (e) {
      console.error('[checkPermissions]', e);
    }
  }

  async logout() {
    this.drawerService.close();
    await this.authService.logout();
    this.router.navigate(['/home']);
  }

  // ── QR Modal ────────────────────────────────────────────────────

  openQrModal(tab: 'scan' | 'show' = 'scan') {
    this.qrTab.set(tab);
    this.scanState.set('scanning');
    this.scanHandled = false;
    this.scanMsg.set('');
    this.qrModalOpen.set(true);
    if (tab === 'scan') {
      setTimeout(() => this.startScanner(), 150);
    } else {
      this.loadMyQr();
    }
  }

  async closeQrModal() {
    await this.stopScanner();
    this.qrModalOpen.set(false);
    this.scanState.set('scanning');
    this.scanHandled = false;
    this.scanMsg.set('');
  }

  async switchQrTab(tab: 'scan' | 'show') {
    if (this.qrTab() === tab) return;
    if (this.qrTab() === 'scan') {
      await this.stopScanner();
    }
    this.qrTab.set(tab);
    if (tab === 'scan') {
      this.scanState.set('scanning');
      this.scanHandled = false;
      this.scanMsg.set('');
      setTimeout(() => this.startScanner(), 150);
    } else {
      this.loadMyQr();
    }
  }

  private async startScanner() {
    try {
      this.qrScanner = new Html5Qrcode('qr-modal-scanner-region');
      await this.qrScanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (text: string) => this.onScanSuccess(text),
        () => {},
      );
    } catch {
      this.scanState.set('error');
      this.scanMsg.set('No se pudo acceder a la cámara. Verifica los permisos.');
    }
  }

  private async stopScanner() {
    try {
      if (this.qrScanner?.isScanning) {
        await this.qrScanner.stop();
      }
    } catch {}
  }

  private async onScanSuccess(text: string) {
    if (this.scanHandled) return;
    this.scanHandled = true;
    await this.stopScanner();

    const match = text.match(/^TRIP:(\d+)$/);
    if (!match) {
      this.scanState.set('error');
      this.scanMsg.set('Código QR no reconocido. Asegúrate de escanear el QR del conductor.');
      return;
    }

    const tripId = parseInt(match[1], 10);
    let lat: number | undefined;
    let lng: number | undefined;
    try {
      const pos = await Geolocation.getCurrentPosition({ timeout: 5000, enableHighAccuracy: true });
      lat = pos.coords.latitude;
      lng = pos.coords.longitude;
    } catch {}

    try {
      const result = await this.boardingService.checkin(tripId, lat, lng);

      // Auto-pay if toggle was on and amount doesn't exceed balance
      if (this.paymentIntent.isReady() && !this.payAmountExceedsBalance()) {
        try {
          await this.boardingService.payWithBalance(result.boardingId, this.paymentIntent.amount());
        } catch { /* payment failure doesn't block check-in */ }
        this.paymentIntent.reset();
        void this.regenerateQr(); // reset QR to plain PAX:token
      }

      this.scanState.set('success');
      this.scanMsg.set(result.message);
      const boarding: ActiveBoarding = {
        boardingId: result.boardingId,
        tripId,
        lineName:  result.lineName,
        plate:     result.plate,
        scannedAt: new Date().toISOString(),
      };
      await this.boardingService.saveActiveBoarding(boarding);
      this.activeBoarding.set(boarding);
    } catch (err: any) {
      const status = err?.status;
      if (status === 409) {
        this.scanState.set('duplicate');
        this.scanMsg.set('Ya registraste tu check-in en este recorrido.');
      } else if (status === 403) {
        this.scanState.set('error');
        this.scanMsg.set('Este recorrido ya no acepta check-in.');
      } else {
        this.scanState.set('error');
        this.scanMsg.set('Error al registrar el check-in. Intenta de nuevo.');
      }
    }
  }

  async retryScanner() {
    this.scanHandled = false;
    this.scanState.set('scanning');
    this.scanMsg.set('');
    setTimeout(() => this.startScanner(), 150);
  }

  // ── Check-out ───────────────────────────────────────────────────

  async checkOut() {
    const boarding = this.activeBoarding();
    if (!boarding || this.checkoutLoading()) return;
    this.checkoutLoading.set(true);
    try {
      let lat: number | undefined;
      let lng: number | undefined;
      try {
        const pos = await Geolocation.getCurrentPosition({ timeout: 4000, enableHighAccuracy: true });
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      } catch {}
      await this.boardingService.checkout(boarding.boardingId, lat, lng);
      await this.boardingService.clearActiveBoarding();
      this.activeBoarding.set(null);
    } catch (err: any) {
      // 409 = already checked out (e.g. trip ended) — clear banner anyway
      if (err?.status === 409 || err?.status === 404) {
        await this.boardingService.clearActiveBoarding();
        this.activeBoarding.set(null);
      }
      // Other errors: silently ignore so user can retry
    } finally {
      this.checkoutLoading.set(false);
    }
  }

  private async loadMyQr() {
    if (this.qrDataUrl()) return; // already loaded — reuse
    this.qrLoading.set(true);
    this.qrError.set('');
    try {
      this.qrToken = await this.boardingService.getMyQrToken();
      await this.regenerateQr();
    } catch {
      this.qrError.set('No se pudo cargar tu código QR. Intenta de nuevo.');
    } finally {
      this.qrLoading.set(false);
    }
  }

  private async regenerateQr() {
    if (!this.qrToken) return;
    const { enabled, amount } = { enabled: this.paymentIntent.enabled(), amount: this.paymentIntent.amount() };
    const payload = enabled && amount > 0
      ? `PAX:${this.qrToken}:PAY:${amount.toFixed(2)}`
      : `PAX:${this.qrToken}`;
    try {
      const dataUrl = await QRCode.toDataURL(payload, {
        width: 240,
        margin: 2,
        color: { dark: '#1e293b', light: '#ffffff' },
        errorCorrectionLevel: 'M',
      });
      this.qrDataUrl.set(dataUrl);
    } catch {}
  }

  onPayToggle(enabled: boolean) {
    this.paymentIntent.toggle(enabled);
    if (!enabled) this.paymentIntent.setAmount(0);
    void this.regenerateQr();
  }

  onPayAmountChange(raw: string) {
    const val = parseFloat(raw);
    this.paymentIntent.setAmount(isNaN(val) || val < 0 ? 0 : val);
    void this.regenerateQr();
  }
}
